import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { buscarComboPorCodigo } from "../../services/auth.service";
import { exportarRelatorioIcmsSt } from "../../services/api-relatorios.service";
import MensagemErro from "../MensagemErro";
import styles from "../BotaoRelatorio/styles.module.css";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  CHAVES_ICMS_ST,
  deduplicarFiltrosIcmsSt,
  getChavesExibicaoIcmsSt,
  ordenarChavesDinamicasIcmsSt,
  rotuloFiltroIcmsSt,
} from "../../utils/extracaoIcmsStFiltros.util";

const FORMATO_DATA = /^\d{4}-\d{2}-\d{2}$/;

/** Mesmo padrão de Switch usado em Previa ICMS / cadastros (#18316E). */
const SX_SWITCH_ICMS_ST = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#18316E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#18316E",
  },
};

/**
 * Duas opções com `Switch`: esquerda = desligado, direita = ligado.
 * Grupo alinhado à esquerda (texto · switch · texto).
 */
function LinhaSwitchBinario({
  labelEsquerda,
  labelDireita,
  checked,
  onChange,
  ariaLabel,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: !checked ? "primary.main" : "text.secondary",
          fontWeight: !checked ? 600 : 400,
        }}
      >
        {labelEsquerda}
      </Typography>
      <Switch
        size="small"
        checked={checked}
        onChange={onChange}
        inputProps={{ "aria-label": ariaLabel }}
        sx={{ ...SX_SWITCH_ICMS_ST, flexShrink: 0 }}
      />
      <Typography
        variant="body2"
        sx={{
          color: checked ? "primary.main" : "text.secondary",
          fontWeight: checked ? 600 : 400,
        }}
      >
        {labelDireita}
      </Typography>
    </Box>
  );
}

async function carregarCombo(codCombo) {
  const response = await buscarComboPorCodigo(codCombo);
  const combosFormat = (response.detalhes.itensCombo || [])
    .filter((item) => item)
    .map((item) => {
      const split = item.split("/|/");
      return { id: split[0], detalhes: split[1] };
    });
  return combosFormat.filter(
    (combo, index, self) =>
      index === self.findIndex((t) => t.detalhes === combo.detalhes)
  );
}

/**
 * Formulário ICMS-ST: grupo → empresa (schema), depois filtros conforme `relatorios_filtros`.
 * @param {Array<{ nome?: string, variavel?: string, tipoDado?: string }>|undefined} [filtros] — vazio = exibe todos os campos dinâmicos (legado).
 */
export default function ExtracaoIcmsStForm({
  idRelatorio,
  filtros: filtrosRelatorio,
  onClose,
  onFinished,
}) {
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [listaEmpresas, setListaEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [notasSaida, setNotasSaida] = useState("Cruzadas");
  const [prodId, setProdId] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackErro, setSnackErro] = useState({ open: false, msg: "" });

  const modoRestrito = Array.isArray(filtrosRelatorio) && filtrosRelatorio.length > 0;
  const filtrosDedup = useMemo(
    () => deduplicarFiltrosIcmsSt(filtrosRelatorio),
    [filtrosRelatorio]
  );
  const chavesVisiveis = useMemo(
    () => getChavesExibicaoIcmsSt(filtrosRelatorio),
    [filtrosRelatorio]
  );
  const chavesOrdenadas = useMemo(
    () => ordenarChavesDinamicasIcmsSt(chavesVisiveis),
    [chavesVisiveis]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lista = await carregarCombo("GRUPOECONOMICO");
        if (!cancelled) setGrupos(lista);
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!grupoSelecionado) {
      setListaEmpresas([]);
      setEmpresaSelecionada(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const lista = await carregarCombo(
          `EMPRESA_POR_GRUPO_${grupoSelecionado.id}`
        );
        if (!cancelled) {
          setListaEmpresas(lista);
          setEmpresaSelecionada(null);
        }
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [grupoSelecionado]);

  const handleGerar = async () => {
    if (
      modoRestrito &&
      (!chavesVisiveis.has(CHAVES_ICMS_ST.dataInicial) ||
        !chavesVisiveis.has(CHAVES_ICMS_ST.dataFinal))
    ) {
      setSnackErro({
        open: true,
        msg:
          "O cadastro do relatório deve incluir os filtros de data inicial e data final.",
      });
      return;
    }

    const idRelatorioNum = Number(idRelatorio);
    if (idRelatorio == null || idRelatorio === "" || !Number.isFinite(idRelatorioNum)) {
      setSnackErro({
        open: true,
        msg:
          "Relatório inválido. Feche e abra novamente ou atualize a página.",
      });
      return;
    }

    if (!grupoSelecionado) {
      setSnackErro({
        open: true,
        msg: "Selecione o grupo econômico.",
      });
      return;
    }

    if (chavesVisiveis.has(CHAVES_ICMS_ST.dataInicial)) {
      if (!dataInicial || !FORMATO_DATA.test(dataInicial)) {
        setSnackErro({
          open: true,
          msg: "Informe a data inicial (YYYY-MM-DD).",
        });
        return;
      }
    }
    if (chavesVisiveis.has(CHAVES_ICMS_ST.dataFinal)) {
      if (!dataFinal || !FORMATO_DATA.test(dataFinal)) {
        setSnackErro({
          open: true,
          msg: "Informe a data final (YYYY-MM-DD).",
        });
        return;
      }
    }

    if (
      chavesVisiveis.has(CHAVES_ICMS_ST.dataInicial) &&
      chavesVisiveis.has(CHAVES_ICMS_ST.dataFinal) &&
      dataInicial &&
      dataFinal &&
      FORMATO_DATA.test(dataInicial) &&
      FORMATO_DATA.test(dataFinal) &&
      dataFinal < dataInicial
    ) {
      setSnackErro({
        open: true,
        msg: "A data final não pode ser anterior à data inicial.",
      });
      return;
    }

    if (!empresaSelecionada?.id) {
      if (listaEmpresas.length === 0) {
        setSnackErro({
          open: true,
          msg: "Não há empresas disponíveis para o grupo selecionado.",
        });
      } else {
        setSnackErro({
          open: true,
          msg: "Selecione uma empresa.",
        });
      }
      return;
    }

    const notasSaidaEnvio = chavesVisiveis.has(CHAVES_ICMS_ST.notasSaida)
      ? notasSaida
      : "Cruzadas";
    const prodIdEnvio = chavesVisiveis.has(CHAVES_ICMS_ST.prodId)
      ? prodId
      : false;
    const dataInicialEnvio = chavesVisiveis.has(CHAVES_ICMS_ST.dataInicial)
      ? dataInicial
      : "";
    const dataFinalEnvio = chavesVisiveis.has(CHAVES_ICMS_ST.dataFinal)
      ? dataFinal
      : "";

    const schema = `z_${empresaSelecionada.id}`;
    setLoading(true);
    try {
      const res = await exportarRelatorioIcmsSt({
        idRelatorio: idRelatorioNum,
        params: {
          dataInicial: dataInicialEnvio,
          dataFinal: dataFinalEnvio,
          schema,
          notasSaida: notasSaidaEnvio,
          prodId: prodIdEnvio,
        },
      });
      if (res?.sucesso) {
        if (onFinished) onFinished(true, "Relatório ICMS-ST gerado com sucesso.");
        else onClose();
      } else {
        setSnackErro({
          open: true,
          msg: res?.mensagem || "Não foi possível gerar o relatório.",
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.mensagem ||
        err?.message ||
        "Erro ao gerar o relatório ICMS-ST.";
      setSnackErro({ open: true, msg });
    } finally {
      setLoading(false);
    }
  };

  const renderCampoDinamico = (chave) => {
    switch (chave) {
      case CHAVES_ICMS_ST.dataInicial: {
        const labelIni = rotuloFiltroIcmsSt(
          filtrosDedup,
          CHAVES_ICMS_ST.dataInicial,
          "Data inicial"
        );
        const labelFim = rotuloFiltroIcmsSt(
          filtrosDedup,
          CHAVES_ICMS_ST.dataFinal,
          "Data final"
        );
        const ambasDatas =
          chavesVisiveis.has(CHAVES_ICMS_ST.dataInicial) &&
          chavesVisiveis.has(CHAVES_ICMS_ST.dataFinal);
        if (ambasDatas) {
          return (
            <Box
              key="datas-icms-st"
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                width: 1,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label={labelIni}
                  type="date"
                  value={dataInicial}
                  onChange={(e) => setDataInicial(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label={labelFim}
                  type="date"
                  value={dataFinal}
                  onChange={(e) => setDataFinal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            </Box>
          );
        }
        return (
          <TextField
            key={chave}
            label={labelIni}
            type="date"
            value={dataInicial}
            onChange={(e) => setDataInicial(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        );
      }
      case CHAVES_ICMS_ST.dataFinal:
        if (
          chavesVisiveis.has(CHAVES_ICMS_ST.dataInicial) &&
          chavesVisiveis.has(CHAVES_ICMS_ST.dataFinal)
        ) {
          return null;
        }
        return (
          <TextField
            key={chave}
            label={rotuloFiltroIcmsSt(
              filtrosDedup,
              CHAVES_ICMS_ST.dataFinal,
              "Data final"
            )}
            type="date"
            value={dataFinal}
            onChange={(e) => setDataFinal(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        );
      case CHAVES_ICMS_ST.notasSaida: {
        const rotuloNotas = rotuloFiltroIcmsSt(
          filtrosDedup,
          CHAVES_ICMS_ST.notasSaida,
          "Notas de saída"
        );
        const rotuloProdId = rotuloFiltroIcmsSt(
          filtrosDedup,
          CHAVES_ICMS_ST.prodId,
          "ID Produto"
        );
        const linhaComProdId = chavesVisiveis.has(CHAVES_ICMS_ST.prodId);

        const blocoNotas = (
          <FormControl component="fieldset" fullWidth variant="standard">
            <FormLabel component="legend" sx={{ mb: 0.75, fontSize: "0.875rem" }}>
              {rotuloNotas}
            </FormLabel>
            <LinhaSwitchBinario
              labelEsquerda="Cruzadas"
              labelDireita="Todas"
              checked={notasSaida === "Todas"}
              onChange={(e) =>
                setNotasSaida(e.target.checked ? "Todas" : "Cruzadas")
              }
              ariaLabel={rotuloNotas}
            />
          </FormControl>
        );

        const blocoProdId = (
          <FormControl component="fieldset" fullWidth variant="standard">
            <FormLabel component="legend" sx={{ mb: 0.75, fontSize: "0.875rem" }}>
              {rotuloProdId}
            </FormLabel>
            <LinhaSwitchBinario
              labelEsquerda="SIM"
              labelDireita="Não"
              checked={!prodId}
              onChange={(e) => setProdId(!e.target.checked)}
              ariaLabel={rotuloProdId}
            />
          </FormControl>
        );

        if (linhaComProdId) {
          return (
            <Box
              key="icms-st-notas-prod"
              sx={{
                display: "grid",
                width: "100%",
                gap: { xs: 2, sm: 4 },
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ minWidth: 0, width: "100%" }}>{blocoNotas}</Box>
              <Box sx={{ minWidth: 0, width: "100%" }}>{blocoProdId}</Box>
            </Box>
          );
        }

        return <Box key={chave}>{blocoNotas}</Box>;
      }
      case CHAVES_ICMS_ST.prodId: {
        if (chavesVisiveis.has(CHAVES_ICMS_ST.notasSaida)) {
          return null;
        }
        const rotuloProd = rotuloFiltroIcmsSt(
          filtrosDedup,
          CHAVES_ICMS_ST.prodId,
          "ID Produto"
        );
        return (
          <FormControl key={chave} component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 0.75, fontSize: "0.875rem" }}>
              {rotuloProd}
            </FormLabel>
            <LinhaSwitchBinario
              labelEsquerda="SIM"
              labelDireita="Não"
              checked={!prodId}
              onChange={(e) => setProdId(!e.target.checked)}
              ariaLabel={rotuloProd}
            />
          </FormControl>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Autocomplete
          options={grupos}
          value={grupoSelecionado}
          onChange={(_, v) => setGrupoSelecionado(v)}
          getOptionLabel={(o) => o.detalhes}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          renderInput={(params) => (
            <TextField {...params} label="Grupo econômico" />
          )}
        />

        {listaEmpresas.length > 0 && (
          <Autocomplete
            options={listaEmpresas}
            value={empresaSelecionada}
            onChange={(_, v) => setEmpresaSelecionada(v)}
            getOptionLabel={(o) => o.detalhes}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => (
              <TextField {...params} label="Empresa" />
            )}
          />
        )}

        {chavesOrdenadas.map((chave) => renderCampoDinamico(chave))}

        <div className={styles.button_container}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={handleGerar}
            disabled={loading}
            sx={{ backgroundColor: "#18316E" }}
          >
            {loading ? "Gerando..." : "Gerar"}
          </Button>
        </div>
      </Box>

      <MensagemErro
        open={snackErro.open}
        handleClose={() => setSnackErro({ open: false, msg: "" })}
        msg={snackErro.msg}
      />
    </>
  );
}
